/**
 * Native-script Jyotish vocabulary for the in-language kundali editions.
 *
 * The engine returns Latin ids ("purva_phalguni", "mars", "vrishchik",
 * "kaal_sarp_dosh"). Hindi screens read `vedic-names-hi.ts`; this is the same
 * idea for the regional editions in `IN_LANGUAGE_KUNDALI_LOCALES`. These are
 * fixed traditional terms, so each language uses its own established form, not
 * a transliteration of the Hindi one:
 *
 * - Bengali follows the panjika: রবি for the Sun, বৃষ, কর্কট, and the -া
 *   nakshatra forms (পুষ্যা, হস্তা, মূলা, শ্রবণা).
 * - Gujarati uses મંગળ, ધન (Sagittarius), મૃગશીર્ષ and મૂળ.
 * - Marathi follows the Marathi panchang: रवि, मंगळ, राहू/केतू, तूळ, मृग,
 *   मूळ and शततारका (Shatabhisha).
 * - Tamil uses its own nakshatra names (திருவாதிரை, பூசம், ஆயில்யம், பூரம்,
 *   உத்திரம், கேட்டை, திருவோணம், அவிட்டம், சதயம், பூரட்டாதி, உத்திரட்டாதி)
 *   and the -ம் rashi forms (மேஷம், ரிஷபம், கடகம்).
 * - Odia uses ବିଛା for Scorpio, as Odia rashiphala does, and the -ା
 *   nakshatra forms shared with Bengali.
 *
 * `jyotish-regional-terms.test.ts` pins every locale to the full id set, and
 * the shared chart's graha names to these.
 */
import type { KundaliRegionalLocale } from "@/i18n/regional-locales";
import { resolveRashi } from "@/lib/rashi-presentation";

/** @internal */
export const GRAHA_IDS = [
  "sun",
  "moon",
  "mars",
  "mercury",
  "jupiter",
  "venus",
  "saturn",
  "rahu",
  "ketu",
] as const;
type GrahaId = (typeof GRAHA_IDS)[number];

/**
 * Rashi ids in zodiac order (1 = mesh … 12 = meen), as `@ishvaram/domain-data` names them.
 * @internal
 */
export const RASHI_IDS = [
  "mesh",
  "vrishabh",
  "mithun",
  "kark",
  "singh",
  "kanya",
  "tula",
  "vrishchik",
  "dhanu",
  "makar",
  "kumbh",
  "meen",
] as const;
type RashiId = (typeof RASHI_IDS)[number];

/** @internal */
export const NAKSHATRA_IDS = [
  "ashwini",
  "bharani",
  "krittika",
  "rohini",
  "mrigashira",
  "ardra",
  "punarvasu",
  "pushya",
  "ashlesha",
  "magha",
  "purva_phalguni",
  "uttara_phalguni",
  "hasta",
  "chitra",
  "swati",
  "vishakha",
  "anuradha",
  "jyeshtha",
  "moola",
  "purva_ashadha",
  "uttara_ashadha",
  "shravana",
  "dhanishta",
  "shatabhisha",
  "purva_bhadrapada",
  "uttara_bhadrapada",
  "revati",
] as const;
type NakshatraId = (typeof NAKSHATRA_IDS)[number];

/**
 * The dosha ids the engine detects (`data/domain/astrology/doshas.yaml`).
 * @internal
 */
export const DOSHA_IDS = [
  "mangal_dosh",
  "shani_dosh",
  "rahu_dosh",
  "ketu_dosh",
  "kaal_sarp_dosh",
  "pitra_dosh",
] as const;
type DoshaId = (typeof DOSHA_IDS)[number];

type Terms = {
  grahas: Record<GrahaId, string>;
  rashis: Record<RashiId, string>;
  nakshatras: Record<NakshatraId, string>;
  doshas: Record<DoshaId, string>;
};

/** @internal */
export const JYOTISH_REGIONAL_TERMS: Record<KundaliRegionalLocale, Terms> = {
  bn: {
    grahas: {
      sun: "রবি",
      moon: "চন্দ্র",
      mars: "মঙ্গল",
      mercury: "বুধ",
      jupiter: "বৃহস্পতি",
      venus: "শুক্র",
      saturn: "শনি",
      rahu: "রাহু",
      ketu: "কেতু",
    },
    rashis: {
      mesh: "মেষ",
      vrishabh: "বৃষ",
      mithun: "মিথুন",
      kark: "কর্কট",
      singh: "সিংহ",
      kanya: "কন্যা",
      tula: "তুলা",
      vrishchik: "বৃশ্চিক",
      dhanu: "ধনু",
      makar: "মকর",
      kumbh: "কুম্ভ",
      meen: "মীন",
    },
    nakshatras: {
      ashwini: "অশ্বিনী",
      bharani: "ভরণী",
      krittika: "কৃত্তিকা",
      rohini: "রোহিণী",
      mrigashira: "মৃগশিরা",
      ardra: "আর্দ্রা",
      punarvasu: "পুনর্বসু",
      pushya: "পুষ্যা",
      ashlesha: "অশ্লেষা",
      magha: "মঘা",
      purva_phalguni: "পূর্বফাল্গুনী",
      uttara_phalguni: "উত্তরফাল্গুনী",
      hasta: "হস্তা",
      chitra: "চিত্রা",
      swati: "স্বাতী",
      vishakha: "বিশাখা",
      anuradha: "অনুরাধা",
      jyeshtha: "জ্যেষ্ঠা",
      moola: "মূলা",
      purva_ashadha: "পূর্বাষাঢ়া",
      uttara_ashadha: "উত্তরাষাঢ়া",
      shravana: "শ্রবণা",
      dhanishta: "ধনিষ্ঠা",
      shatabhisha: "শতভিষা",
      purva_bhadrapada: "পূর্বভাদ্রপদ",
      uttara_bhadrapada: "উত্তরভাদ্রপদ",
      revati: "রেবতী",
    },
    doshas: {
      mangal_dosh: "মাঙ্গলিক দোষ",
      shani_dosh: "শনি দোষ",
      rahu_dosh: "রাহু দোষ",
      ketu_dosh: "কেতু দোষ",
      kaal_sarp_dosh: "কালসর্প দোষ",
      pitra_dosh: "পিতৃ দোষ",
    },
  },
  gu: {
    grahas: {
      sun: "સૂર્ય",
      moon: "ચંદ્ર",
      mars: "મંગળ",
      mercury: "બુધ",
      jupiter: "ગુરુ",
      venus: "શુક્ર",
      saturn: "શનિ",
      rahu: "રાહુ",
      ketu: "કેતુ",
    },
    rashis: {
      mesh: "મેષ",
      vrishabh: "વૃષભ",
      mithun: "મિથુન",
      kark: "કર્ક",
      singh: "સિંહ",
      kanya: "કન્યા",
      tula: "તુલા",
      vrishchik: "વૃશ્ચિક",
      dhanu: "ધન",
      makar: "મકર",
      kumbh: "કુંભ",
      meen: "મીન",
    },
    nakshatras: {
      ashwini: "અશ્વિની",
      bharani: "ભરણી",
      krittika: "કૃત્તિકા",
      rohini: "રોહિણી",
      mrigashira: "મૃગશીર્ષ",
      ardra: "આર્દ્રા",
      punarvasu: "પુનર્વસુ",
      pushya: "પુષ્ય",
      ashlesha: "આશ્લેષા",
      magha: "મઘા",
      purva_phalguni: "પૂર્વા ફાલ્ગુની",
      uttara_phalguni: "ઉત્તરા ફાલ્ગુની",
      hasta: "હસ્ત",
      chitra: "ચિત્રા",
      swati: "સ્વાતિ",
      vishakha: "વિશાખા",
      anuradha: "અનુરાધા",
      jyeshtha: "જ્યેષ્ઠા",
      moola: "મૂળ",
      purva_ashadha: "પૂર્વાષાઢા",
      uttara_ashadha: "ઉત્તરાષાઢા",
      shravana: "શ્રવણ",
      dhanishta: "ધનિષ્ઠા",
      shatabhisha: "શતભિષા",
      purva_bhadrapada: "પૂર્વા ભાદ્રપદ",
      uttara_bhadrapada: "ઉત્તરા ભાદ્રપદ",
      revati: "રેવતી",
    },
    doshas: {
      mangal_dosh: "માંગલિક દોષ",
      shani_dosh: "શનિ દોષ",
      rahu_dosh: "રાહુ દોષ",
      ketu_dosh: "કેતુ દોષ",
      kaal_sarp_dosh: "કાલસર્પ દોષ",
      pitra_dosh: "પિતૃ દોષ",
    },
  },
  mr: {
    grahas: {
      sun: "रवि",
      moon: "चंद्र",
      mars: "मंगळ",
      mercury: "बुध",
      jupiter: "गुरु",
      venus: "शुक्र",
      saturn: "शनि",
      rahu: "राहू",
      ketu: "केतू",
    },
    rashis: {
      mesh: "मेष",
      vrishabh: "वृषभ",
      mithun: "मिथुन",
      kark: "कर्क",
      singh: "सिंह",
      kanya: "कन्या",
      tula: "तूळ",
      vrishchik: "वृश्चिक",
      dhanu: "धनु",
      makar: "मकर",
      kumbh: "कुंभ",
      meen: "मीन",
    },
    nakshatras: {
      ashwini: "अश्विनी",
      bharani: "भरणी",
      krittika: "कृत्तिका",
      rohini: "रोहिणी",
      mrigashira: "मृग",
      ardra: "आर्द्रा",
      punarvasu: "पुनर्वसू",
      pushya: "पुष्य",
      ashlesha: "आश्लेषा",
      magha: "मघा",
      purva_phalguni: "पूर्वा फाल्गुनी",
      uttara_phalguni: "उत्तरा फाल्गुनी",
      hasta: "हस्त",
      chitra: "चित्रा",
      swati: "स्वाती",
      vishakha: "विशाखा",
      anuradha: "अनुराधा",
      jyeshtha: "ज्येष्ठा",
      moola: "मूळ",
      purva_ashadha: "पूर्वाषाढा",
      uttara_ashadha: "उत्तराषाढा",
      shravana: "श्रवण",
      dhanishta: "धनिष्ठा",
      shatabhisha: "शततारका",
      purva_bhadrapada: "पूर्वा भाद्रपदा",
      uttara_bhadrapada: "उत्तरा भाद्रपदा",
      revati: "रेवती",
    },
    doshas: {
      mangal_dosh: "मंगळ दोष",
      shani_dosh: "शनि दोष",
      rahu_dosh: "राहू दोष",
      ketu_dosh: "केतू दोष",
      kaal_sarp_dosh: "कालसर्प दोष",
      pitra_dosh: "पितृ दोष",
    },
  },
  or: {
    grahas: {
      sun: "ସୂର୍ଯ୍ୟ",
      moon: "ଚନ୍ଦ୍ର",
      mars: "ମଙ୍ଗଳ",
      mercury: "ବୁଧ",
      jupiter: "ଗୁରୁ",
      venus: "ଶୁକ୍ର",
      saturn: "ଶନି",
      rahu: "ରାହୁ",
      ketu: "କେତୁ",
    },
    rashis: {
      mesh: "ମେଷ",
      vrishabh: "ବୃଷ",
      mithun: "ମିଥୁନ",
      kark: "କର୍କଟ",
      singh: "ସିଂହ",
      kanya: "କନ୍ୟା",
      tula: "ତୁଳା",
      vrishchik: "ବିଛା",
      dhanu: "ଧନୁ",
      makar: "ମକର",
      kumbh: "କୁମ୍ଭ",
      meen: "ମୀନ",
    },
    nakshatras: {
      ashwini: "ଅଶ୍ୱିନୀ",
      bharani: "ଭରଣୀ",
      krittika: "କୃତ୍ତିକା",
      rohini: "ରୋହିଣୀ",
      mrigashira: "ମୃଗଶିରା",
      ardra: "ଆର୍ଦ୍ରା",
      punarvasu: "ପୁନର୍ବସୁ",
      pushya: "ପୁଷ୍ୟା",
      ashlesha: "ଅଶ୍ଳେଷା",
      magha: "ମଘା",
      purva_phalguni: "ପୂର୍ବଫାଲ୍ଗୁନୀ",
      uttara_phalguni: "ଉତ୍ତରଫାଲ୍ଗୁନୀ",
      hasta: "ହସ୍ତା",
      chitra: "ଚିତ୍ରା",
      swati: "ସ୍ୱାତୀ",
      vishakha: "ବିଶାଖା",
      anuradha: "ଅନୁରାଧା",
      jyeshtha: "ଜ୍ୟେଷ୍ଠା",
      moola: "ମୂଳା",
      purva_ashadha: "ପୂର୍ବାଷାଢ଼ା",
      uttara_ashadha: "ଉତ୍ତରାଷାଢ଼ା",
      shravana: "ଶ୍ରବଣା",
      dhanishta: "ଧନିଷ୍ଠା",
      shatabhisha: "ଶତଭିଷା",
      purva_bhadrapada: "ପୂର୍ବଭାଦ୍ରପଦ",
      uttara_bhadrapada: "ଉତ୍ତରଭାଦ୍ରପଦ",
      revati: "ରେବତୀ",
    },
    doshas: {
      mangal_dosh: "ମାଙ୍ଗଳିକ ଦୋଷ",
      shani_dosh: "ଶନି ଦୋଷ",
      rahu_dosh: "ରାହୁ ଦୋଷ",
      ketu_dosh: "କେତୁ ଦୋଷ",
      kaal_sarp_dosh: "କାଳସର୍ପ ଦୋଷ",
      pitra_dosh: "ପିତୃ ଦୋଷ",
    },
  },
  ta: {
    grahas: {
      sun: "சூரியன்",
      moon: "சந்திரன்",
      mars: "செவ்வாய்",
      mercury: "புதன்",
      jupiter: "குரு",
      venus: "சுக்கிரன்",
      saturn: "சனி",
      rahu: "ராகு",
      ketu: "கேது",
    },
    rashis: {
      mesh: "மேஷம்",
      vrishabh: "ரிஷபம்",
      mithun: "மிதுனம்",
      kark: "கடகம்",
      singh: "சிம்மம்",
      kanya: "கன்னி",
      tula: "துலாம்",
      vrishchik: "விருச்சிகம்",
      dhanu: "தனுசு",
      makar: "மகரம்",
      kumbh: "கும்பம்",
      meen: "மீனம்",
    },
    nakshatras: {
      ashwini: "அசுவினி",
      bharani: "பரணி",
      krittika: "கார்த்திகை",
      rohini: "ரோகிணி",
      mrigashira: "மிருகசீரிடம்",
      ardra: "திருவாதிரை",
      punarvasu: "புனர்பூசம்",
      pushya: "பூசம்",
      ashlesha: "ஆயில்யம்",
      magha: "மகம்",
      purva_phalguni: "பூரம்",
      uttara_phalguni: "உத்திரம்",
      hasta: "அஸ்தம்",
      chitra: "சித்திரை",
      swati: "சுவாதி",
      vishakha: "விசாகம்",
      anuradha: "அனுஷம்",
      jyeshtha: "கேட்டை",
      moola: "மூலம்",
      purva_ashadha: "பூராடம்",
      uttara_ashadha: "உத்திராடம்",
      shravana: "திருவோணம்",
      dhanishta: "அவிட்டம்",
      shatabhisha: "சதயம்",
      purva_bhadrapada: "பூரட்டாதி",
      uttara_bhadrapada: "உத்திரட்டாதி",
      revati: "ரேவதி",
    },
    doshas: {
      mangal_dosh: "செவ்வாய் தோஷம்",
      shani_dosh: "சனி தோஷம்",
      rahu_dosh: "ராகு தோஷம்",
      ketu_dosh: "கேது தோஷம்",
      kaal_sarp_dosh: "கால சர்ப்ப தோஷம்",
      pitra_dosh: "பித்ரு தோஷம்",
    },
  },
};

const NAKSHATRA_ALIASES: Readonly<Record<string, NakshatraId>> = {
  mula: "moola",
  shatabhishaj: "shatabhisha",
  dhanishtha: "dhanishta",
};

function key(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
}

function lookup<K extends string>(table: Record<K, string>, id: string): string | null {
  return Object.hasOwn(table, id) ? table[id as K] : null;
}

/** Native graha name for a planet id ("mars") or English name ("Mars"); null when unknown. */
export function regionalGrahaName(
  value: string | null | undefined,
  locale: KundaliRegionalLocale
): string | null {
  return value ? lookup(JYOTISH_REGIONAL_TERMS[locale].grahas, key(value)) : null;
}

/** Native rashi name for any rashi id or alias `resolveRashi` accepts; null when unknown. */
export function regionalRashiName(
  value: string | null | undefined,
  locale: KundaliRegionalLocale
): string | null {
  const rashi = resolveRashi(value);
  return rashi ? lookup(JYOTISH_REGIONAL_TERMS[locale].rashis, rashi.id) : null;
}

/** Native rashi name for a sign number 1–12; null outside that range. */
export function regionalRashiNameByNumber(
  signNumber: number,
  locale: KundaliRegionalLocale
): string | null {
  const id = RASHI_IDS[signNumber - 1];
  return id ? JYOTISH_REGIONAL_TERMS[locale].rashis[id] : null;
}

/** Native nakshatra name for an engine id or English name; null when unknown. */
export function regionalNakshatraName(
  value: string | null | undefined,
  locale: KundaliRegionalLocale
): string | null {
  if (!value) return null;
  const id = key(value);
  return lookup(JYOTISH_REGIONAL_TERMS[locale].nakshatras, NAKSHATRA_ALIASES[id] ?? id);
}

/** Native dosha name for an engine dosha id; null for an id this table does not know. */
export function regionalDoshaName(
  doshaId: string | null | undefined,
  locale: KundaliRegionalLocale
): string | null {
  return doshaId ? lookup(JYOTISH_REGIONAL_TERMS[locale].doshas, key(doshaId)) : null;
}
